import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { BlogCategory } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { slugify } from '@/features/products';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  emptyBlogCategoryForm,
  fromBlogCategory,
  toBlogCategoryCreate,
  toBlogCategoryUpdate,
} from '../api/mappers';
import { useCreateBlogCategory, useUpdateBlogCategory } from '../hooks/use-blog-category-mutations';
import {
  blogCategoryFormSchema,
  MAX_CATEGORY_POSITION,
  type BlogCategoryForm,
} from '../schemas/blog-category-form-schema';
import { HANDLE } from '../schemas/blog-post-form-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['title', 'handle', 'description', 'position']);

export interface BlogCategoryDialogProps {
  storeId: string;
  open: boolean;
  /** Null adds a new category. */
  category: BlogCategory | null;
  /** Prefilled position of a new category: after the last one. */
  nextPosition: number;
  onOpenChange: (open: boolean) => void;
}

function CategoryFormBody({
  storeId,
  category,
  nextPosition,
  onClose,
}: {
  storeId: string;
  category: BlogCategory | null;
  nextPosition: number;
  onClose: () => void;
}) {
  const { t } = useTranslation(['blog', 'common']);
  const message = useErrorMessage();
  const create = useCreateBlogCategory(storeId);
  const update = useUpdateBlogCategory(storeId);
  const pending = create.isPending || update.isPending;
  const [initial] = useState(() =>
    category ? fromBlogCategory(category) : emptyBlogCategoryForm(nextPosition),
  );
  const form = useForm<BlogCategoryForm>({
    resolver: useI18nResolver(blogCategoryFormSchema),
    defaultValues: initial,
  });
  const [handleTouched, setHandleTouched] = useState(category !== null);
  const handle = form.watch('handle').trim();
  const renamed = category !== null && handle !== category.handle && HANDLE.test(handle);
  const rootError = form.formState.errors.root?.server?.message;

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      if (category) {
        const body = toBlogCategoryUpdate(values, initial);
        if (Object.keys(body).length > 0) await update.mutateAsync({ id: category.id, body });
      } else {
        await create.mutateAsync(toBlogCategoryCreate(values));
      }
      onClose();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      const limit =
        isApiError(error) && error.code === 'LIMIT_EXCEEDED'
          ? (error.meta as { limit?: number } | undefined)?.limit
          : undefined;
      form.setError('root.server', {
        type: 'server',
        message:
          limit !== undefined ? t('blog:categories.limitReached', { limit }) : message(error),
      });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {rootError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{rootError}</AlertDescription>
          </Alert>
        ) : null}
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('blog:fields.title')}</FormLabel>
              <FormControl>
                <Input
                  autoFocus
                  autoComplete="off"
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    if (!handleTouched) {
                      form.setValue('handle', slugify(e.target.value), { shouldDirty: true });
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="handle"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('blog:fields.handle')}</FormLabel>
              <FormControl>
                <Input
                  autoComplete="off"
                  className="font-mono text-[13px]"
                  {...field}
                  onChange={(e) => {
                    setHandleTouched(true);
                    field.onChange(e);
                  }}
                />
              </FormControl>
              <FormDescription>
                {t('blog:categories.handleHint', { handle: handle || '…' })}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        {renamed ? (
          <Alert role="status">
            <AlertDescription>
              {t('blog:rename.category', {
                from: `/blog/category/${category.handle}`,
                to: `/blog/category/${handle}`,
              })}
            </AlertDescription>
          </Alert>
        ) : null}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('blog:fields.description')}</FormLabel>
              <FormControl>
                <Textarea rows={3} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="position"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('blog:fields.position')}</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={MAX_CATEGORY_POSITION}
                  className="w-32"
                  {...field}
                />
              </FormControl>
              <FormDescription>{t('blog:fields.positionHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={pending}>
            {pending
              ? t('common:actions.saving')
              : category
                ? t('common:actions.save')
                : t('blog:categories.create')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/** Adds or edits a category. The form remounts per open so every edit starts from the row. */
export function BlogCategoryDialog({
  storeId,
  open,
  category,
  nextPosition,
  onOpenChange,
}: BlogCategoryDialogProps) {
  const { t } = useTranslation('blog');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {category ? t('categories.editTitle') : t('categories.createTitle')}
          </DialogTitle>
          <DialogDescription>{t('categories.dialogDescription')}</DialogDescription>
        </DialogHeader>
        {open ? (
          <CategoryFormBody
            key={category?.id ?? 'new'}
            storeId={storeId}
            category={category}
            nextPosition={nextPosition}
            onClose={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
